import * as React from 'react'
import { Richtext } from 'storyblok-js-client'
import {
  MARK_BOLD,
  MARK_CODE,
  MARK_ITALIC,
  MARK_LINK,
  MARK_STRIKE,
  MARK_STYLED,
  MARK_UNDERLINE,
  NODE_BR,
  NODE_CODEBLOCK,
  NODE_HEADING,
  NODE_HR,
  NODE_IMAGE,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_QUOTE,
  NODE_UL,
  render,
} from 'storyblok-rich-text-react-renderer'
import { CheckListItem, StyledCheckList } from '../../components/UI/CheckListItem'
import { HelpListItem, StyledHelpList } from '../../components/UI/HelpListItem'
import { Button } from '../../components/UI/Button'
import CustomLink from '../../components/CustomLink'
import { useI18n } from '../context/i18n'

export const renderRichtext = (
  content: Richtext,
  options?: {
    markResolvers?: { [key: string]: ((children: React.ReactNode) => JSX.Element | null) | undefined }
    nodeResolvers?: { [key: string]: ((children: React.ReactNode) => JSX.Element | null) | undefined }
    blockResolvers?: { [key: string]: ((children: React.ReactNode) => JSX.Element | null) | undefined }
  },
) => {
  const markResolvers = options?.markResolvers || {}
  const nodeResolvers = options?.nodeResolvers || {}
  const blockResolvers = options?.blockResolvers || {}


  return render(content, {
    markResolvers: {
      [MARK_LINK]: (children, props) => {
        const { linktype, href, target } = props
        return <CustomLink to={href}>{children}</CustomLink>
      },
      [MARK_BOLD]: children => {
        return <b>{children}</b>
      },
      [MARK_ITALIC]: children => {
        return <i>{children}</i>
      },
      [MARK_STRIKE]: children => {
        return <s>{children}</s>
      },
      [MARK_UNDERLINE]: children => {
        return <u>{children}</u>
      },
      [MARK_CODE]: children => {
        return <code className="code-snippet">{children}</code>
      },
      [MARK_STYLED]: (children, props) => {
        return <span className={props.class}>{children}</span>
      },
      ...markResolvers,
    },
    nodeResolvers: {
      [NODE_HEADING]: (children, props) => {
        switch (props.level) {
          case 1: return <h1 className="is-prose">{children}</h1>
          case 2: return <h2 className="is-prose">{children}</h2>
          case 3: return <h3 className="is-prose">{children}</h3>
          case 4: return <h4 className="is-prose">{children}</h4>
          case 5: return <h5 className="is-prose">{children}</h5>
          case 6: return <h6 className="is-prose">{children}</h6>
          default: return null
        }
      },
      [NODE_CODEBLOCK]: (children, props) => {
        return (
          <pre className="is-prose">
            <code className={props.class}>{children}</code>
          </pre>
        )
      },
      [NODE_IMAGE]: (children, props) => {
        return (
          <span className="is-prose" role="figure">
            <img
              src={props.src}
              alt={props.alt ? props.alt : props.title}
              title={props.alt ? props.alt : props.title}
            />
            {props.title && (
              <span role="figurecaption">
                {props.title}
              </span>
            )}
          </span>
        )
      },
      [NODE_PARAGRAPH]: children => {
        return <p className="is-prose">{children}</p>
      },
      [NODE_QUOTE]: children => {
        return <blockquote className="is-prose">{children}</blockquote>
      },
      [NODE_OL]: children => {
        return <ol className="is-prose">{children}</ol>
      },
      [NODE_UL]: children => {
        return <ul className="is-prose">{children}</ul>
      },
      [NODE_LI]: children => {
        return <li className="is-prose">{children}</li>
      },
      [NODE_HR]: () => {
        return <hr className="is-prose" />
      },
      [NODE_BR]: () => {
        return <br className="is-prose" />
      },
      ...nodeResolvers,
    },
    blockResolvers: {
      ['CheckList']: props => {
        return render(props.CheckList, {
          nodeResolvers: {
            [NODE_UL]: children => {
              return <StyledCheckList>{children}</StyledCheckList>
            },
            [NODE_LI]: children => {
              return <CheckListItem>{children}</CheckListItem>
            },
          },
        })
      },
      ['HelpList']: props => {
        return render(props.HelpList, {
          nodeResolvers: {
            [NODE_UL]: children => {
              return <StyledHelpList>{children}</StyledHelpList>
            },
            [NODE_LI]: children => {
              return <HelpListItem>{children}</HelpListItem>
            },
          },
        })
      },
      ['Button']: props => {
        const { getTranslatedUrlFromLink } = useI18n()
        return (
          <Button
            as={CustomLink}
            to={getTranslatedUrlFromLink(props.link)}
            key={props.link.id}
            $variant={props.button_style}
            $size={props?.size?.length ? props.size : 'default'}
            title={props.link_title}
            $ignoreTheme={props.ignoreTheme}
            {...(props.link_target && { target: '_blank' })}
          >
            {props.button_name}
          </Button>
        )
      },
      ['Spacer']: () => {
        return <div style={{ lineHeight: 0, height: '1rem' }}></div>
      },
      ...blockResolvers,
    },
    defaultStringResolver: string => <p className="is-prose">{string}</p>,
  })
}
